import { Case } from '../case';
import { SimulationVariable } from '../simulation-variable';
// @ts-ignore
import { defaultDCUModel } from '../../_config/unit-operations/default-models/default-dcu-model';
import { ConstrainableObject } from '../_interfaces/constrainable-object';
import { UnitOperationConstraints } from './unit-operation-constraints';
import { MultiPeriodParameterProvider } from '../_multi-period/multi-period-parameter-provider';
import { SimulationVariableName } from '../../_config/simulation-variable-name.enum';
import { KpiProvider } from '../_case-study/kpi-provider';
import { GasContributorUnitOperation } from './gas-contributor-unit-operation';
import { Quantity } from '../../_config/quantity.enum';
import { ParameterProvider } from '../_case-study/parameter-provider';
import { unitOperationsConfig } from '../../_config/unit-operations.config';

declare let unitConverter: any;

export class DCU
  extends GasContributorUnitOperation
  implements ConstrainableObject, MultiPeriodParameterProvider, KpiProvider, ParameterProvider
{
  category = unitOperationsConfig.dcu.key;
  volumetricYield: SimulationVariable;
  producedCoke: SimulationVariable;
  useCustomModel: boolean;
  modelScript: string;
  h2sProductionRate: SimulationVariable;
  h2sProduction: SimulationVariable;
  constraints: UnitOperationConstraints;
  minimumAPI: SimulationVariable;
  maximumCCR: SimulationVariable;
  cokeEmissionFactor: SimulationVariable;
  percentCokeToCombustion: SimulationVariable;

  constructor(unitOperation: any, ownerCase: Case) {
    super(unitOperation, ownerCase);
    this.initValues(unitOperation);
    this.setSimulationVariableNames();
  }

  getAvailableKpis(): SimulationVariable[] {
    if (this.ownerCase.multiPeriodEnabled) {
      return [
        this.ghgEmissions,
        this.ghgIntensity,
        this.volumetricYield,
        this.producedCoke,
        this.h2sProductionRate,
        this.h2sProduction,
        this.opexTotal,
        this.capexTotal,
        this.percentCokeToCombustion,
        this.cokeEmissionFactor,
      ];
    }
    return [
      this.ghgEmissions,
      this.ghgIntensity,
      this.volumetricYield,
      this.producedCoke,
      this.h2sProductionRate,
      this.h2sProduction,
      this.steamUse900,
      this.steamMake900,
      this.steamMake600,
      this.steamUse600,
      this.steamMake150,
      this.steamUse150,
      this.steamMake50,
      this.steamUse50,
      this.opexTotal,
      this.capexTotal,
      this.percentCokeToCombustion,
      this.cokeEmissionFactor,
      this.fuelGasHeatingValue,
      this.fuelGasEmissionFactor,
      this.energyFlow,
      this.flowrate,
    ];
  }

  getAvailableParametricStudyParameters(): SimulationVariable[] {
    return [
      this.percentCokeToCombustion,
      this.cokeEmissionFactor,
      this.h2sProductionRate,
      this.opexVar,
      this.opexGasDiesel,
      this.opexPower,
      this.opexFixed,
      this.capexAmortized,
    ];
  }

  getAvailableParameters() {
    return undefined;
  }

  getAlternativeGhgIntensity(): SimulationVariable {
    return undefined;
  }

  override initValues(unitOperation: any): void {
    super.initValues(unitOperation);
    this.volumetricYield = new SimulationVariable(unitOperation.volumetricYield, this.ownerCase, this);
    this.producedCoke = new SimulationVariable(unitOperation.producedCoke, this.ownerCase, this);
    this.h2sProductionRate = new SimulationVariable(unitOperation.h2sProductionRate || {}, this.ownerCase, this);
    this.h2sProduction = new SimulationVariable(unitOperation.h2sProduction || {}, this.ownerCase, this);
    this.minimumAPI = new SimulationVariable(unitOperation.minimumAPI || {}, this.ownerCase, this);
    this.maximumCCR = new SimulationVariable(unitOperation.maximumCCR || {}, this.ownerCase, this);
    this.cokeEmissionFactor = new SimulationVariable(unitOperation.cokeEmissionFactor || {}, this.ownerCase, this);
    this.percentCokeToCombustion = new SimulationVariable(
      unitOperation.percentCokeToCombustion || {},
      this.ownerCase,
      this
    );
    if (typeof unitOperation.useCustomModel === 'undefined') {
      this.useCustomModel = false;
    } else {
      this.useCustomModel = unitOperation.useCustomModel;
    }
    this.modelScript = typeof unitOperation.modelScript === 'undefined' ? defaultDCUModel : unitOperation.modelScript;

    this.initConstraints();
  }

  initConstraints() {
    this.constraints = new UnitOperationConstraints();
    this.constraints.addConstraint('capacity', this.capacity);
    this.constraints.addConstraint('minimumFlow', this.minimumFlow);
    this.constraints.addConstraint('minimumAPI', this.minimumAPI);
    this.constraints.addConstraint('maximumCCR', this.maximumCCR);
  }

  override setQuantities() {
    super.setQuantities();
    this.volumetricYield.setQuantity('Percent');
    this.producedCoke.setQuantity('Massflowrate');
    this.h2sProduction.setQuantity('Moleflowrate');
    this.h2sProductionRate.setQuantity('GasStdVolumePerVolume');
    this.minimumAPI.setQuantity('LiquidDensity');
    this.maximumCCR.setQuantity('MassFraction');
    this.percentCokeToCombustion.setQuantity(Quantity.PERCENT);
    this.cokeEmissionFactor.setQuantity(Quantity.MASS_EMISSIONS_PER_FUEL_MASS);
  }

  override setDefaultValues(): void {
    super.setDefaultValues();
    if (this.cokeEmissionFactor.isUndefined()) {
      this.cokeEmissionFactor.setDefaultValue(3386.3);
    }
    if (this.percentCokeToCombustion.isUndefined()) {
      this.percentCokeToCombustion.setDefaultValue(15.0);
    }
    if (this.energyFlow.isUndefined()) {
      this.energyFlow.setDefaultValue(0.0, unitConverter.units.Energyrate.GJ_H);
    }
  }

  override dePersist(sup: any) {
    super.dePersist(sup);
    this.volumetricYield = this.getSimulationVariableSafe(sup.volumetricYield);
    this.producedCoke = this.getSimulationVariableSafe(sup.producedCoke);
    this.h2sProduction = this.getSimulationVariableSafe(sup.h2sProduction);
    this.h2sProductionRate = this.getSimulationVariableSafe(sup.h2sProductionRate);
    this.minimumAPI = this.getSimulationVariableSafe(sup.minimumAPI);
    this.maximumCCR = this.getSimulationVariableSafe(sup.maximumCCR);
    this.cokeEmissionFactor = this.getSimulationVariableSafe(sup.cokeEmissionFactor);
    this.percentCokeToCombustion = this.getSimulationVariableSafe(sup.percentCokeToCombustion);
    this.setQuantities();
    this.setDefaultValues();
    this.initConstraints();
  }

  constraintViolated() {
    const inletStream = this.ownerCase.filterSuncorMaterialStreams(s => {
      return s.outletUnitOperationId === this.id;
    })[0];

    return inletStream && this.constraints.constraintValueViolated('capacity', inletStream.volumetricFlowrate.value);
  }

  constraintValueDefined() {
    return this.constraints.anyConstraintValueDefined();
  }

  override setSimulationVariableNames() {
    super.setSimulationVariableNames();
    this.volumetricYield.setName('Volumetric Yield');
    this.producedCoke.setName('Produced Coke');
    this.maximumCCR.setName(SimulationVariableName.MAXIMUM_CCR);
    this.minimumAPI.setName(SimulationVariableName.MINIMUM_API);
    this.opexTotal.setName('Total OPEX');
    this.capexTotal.setName('Total CAPEX');
    this.h2sProductionRate.setName('H2S Production Rate');
    this.h2sProduction.setName('H2S Production');
    this.steamMake900.setName('#900 Steam Make');
    this.steamUse900.setName('#900 Steam Use');
    this.steamMake600.setName('#600 Steam Make');
    this.steamUse600.setName('#600 Steam Use');
    this.steamMake150.setName('#150 Steam Make');
    this.steamUse150.setName('#150 Steam Use');
    this.steamMake50.setName('#50 Steam Make');
    this.steamUse50.setName('#50 Steam Use');

    this.percentCokeToCombustion.setName(SimulationVariableName.PERCENT_COKE_TO_COMBUSTION);
    this.cokeEmissionFactor.setName(SimulationVariableName.COKE_EMISSION_FACTOR);
    this.fuelGasHeatingValue.setName(SimulationVariableName.FUEL_GAS_HEATING_VALUE);
    this.fuelGasEmissionFactor.setName(SimulationVariableName.FUEL_GAS_EMISSION_FACTOR);
    this.energyFlow.setName(SimulationVariableName.ENERGY_FLOW);
    this.flowrate.setName(SimulationVariableName.FUEL_GAS_FLOWRATE);
    this.capacity.setName(SimulationVariableName.CAPACITY);
  }

  getAvailableMultiPeriodParameters(): SimulationVariable[] {
    return [this.capacity];
  }

  override detectChanges(another: any): boolean {
    const changes = super.detectChanges(another);
    return changes || another.modelScript !== this.modelScript;
  }
}
